import { ExperienceResourceArray } from '../Types';
import { FilterOptionData } from '../../Builder/ExperienceCatalog/Types';
import { FilterOptionsPayload } from './FilterOptionsTypes';

// Programs with no set duration are represented as "0" hours
const FLEXIBLE_DURATION_VALUE = '0';

// Rename product-type filter labels for the admin studio. Falls through to the
// server-provided label when the backend eventually emits the new names, so
// this override becomes a no-op without needing a frontend change.
//
// DVLP-7604: keys must match the exact labels emitted by
// EnterprisePortal::FilterOptionsBuilder::PRODUCT_TYPE_LABELS. Note EPC is
// "Advanced Leader Program" (singular) there — the previous plural key never
// matched. Coaching / Group Coaching already come through correctly and need
// no override.
const PRODUCT_TYPE_LABEL_OVERRIDES: Record<string, string> = {
  'Advanced Leader Program': 'Professor-Led Courses',
  'On-Demand Learning': 'On-Demand Courses',
};

export const calculateProductTypeOptions = (
  experiences: ExperienceResourceArray,
  filterOptions: FilterOptionsPayload,
): FilterOptionData[] => {
  const serverOptions = filterOptions.product_type_options;

  if (serverOptions.length === 0) {
    return [];
  }

  const productTypes = experiences.map((exp) => exp.payload.offering_type);

  const grouped = productTypes.reduce(
    (acc, type) => {
      acc[type] = (acc[type] || 0) + 1;
      return acc;
    },
    {} as Record<string, number>,
  );

  return serverOptions
    .map((opt) => ({
      value: opt.value,
      label: PRODUCT_TYPE_LABEL_OVERRIDES[opt.label] ?? opt.label,
      count: grouped[opt.value] || 0,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
};

export const calculateSchoolPartnerOptions = (
  experiences: ExperienceResourceArray,
  filterOptions: FilterOptionsPayload,
): FilterOptionData[] => {
  const serverOptions = filterOptions.school_partner_options;

  if (serverOptions.length === 0) {
    return [];
  }

  const schoolPartners = experiences.map((exp) => exp.payload.school_partner_name);

  const grouped = schoolPartners.reduce(
    (acc, name) => {
      acc[name] = (acc[name] || 0) + 1;
      return acc;
    },
    {} as Record<string, number>,
  );

  return serverOptions
    .map((opt) => ({
      value: opt.value,
      label: opt.label,
      count: grouped[opt.value] || 0,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
};

export const calculateLanguageOptions = (
  experiences: ExperienceResourceArray,
  filterOptions: FilterOptionsPayload,
): FilterOptionData[] => {
  const serverOptions = filterOptions.language_options;

  if (serverOptions.length === 0) {
    return [];
  }

  const allLanguages = experiences.flatMap((exp) => exp.payload.supported_languages);

  const grouped = allLanguages.reduce(
    (acc, lang) => {
      acc[lang] = (acc[lang] || 0) + 1;
      return acc;
    },
    {} as Record<string, number>,
  );

  return serverOptions
    .map((opt) => ({
      value: opt.value,
      label: opt.label,
      count: grouped[opt.value] || 0,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
};

export const calculateDurationOptions = (
  experiences: ExperienceResourceArray,
  filterOptions: FilterOptionsPayload,
): FilterOptionData[] => {
  const serverOptions = filterOptions.duration_options;

  if (serverOptions.length === 0) {
    return [];
  }

  const durations: string[] = [];
  experiences.forEach((exp) => {
    const duration = exp.payload.total_hours
      .map((hours) => hours.toString())
      .getOrElseValue(FLEXIBLE_DURATION_VALUE);
    durations.push(duration);
  });

  const grouped = durations.reduce(
    (acc, hours) => {
      acc[hours] = (acc[hours] || 0) + 1;
      return acc;
    },
    {} as Record<string, number>,
  );

  return serverOptions
    .map((opt) => ({
      value: opt.value,
      label: opt.label,
      count: grouped[opt.value] || 0,
    }))
    .sort((a, b) => parseFloat(a.value) - parseFloat(b.value));
};

export const calculateCompetencyOptions = (
  experiences: ExperienceResourceArray,
): FilterOptionData[] => {
  const competencyCounts: Record<string, number> = {};

  experiences.forEach((exp) => {
    const competencies = exp.payload.competency_names || [];
    competencies.forEach((name) => {
      competencyCounts[name] = (competencyCounts[name] || 0) + 1;
    });
  });

  return Object.entries(competencyCounts)
    .map(([name, count]) => ({
      value: name,
      label: name,
      count,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
};

export const calculateLicenseOptions = (
  experiences: ExperienceResourceArray,
  filterOptions: FilterOptionsPayload,
): FilterOptionData[] => {
  const serverOptions = filterOptions.license_options;

  if (serverOptions.length === 0) {
    return [];
  }

  const licenseTypeIdCounts: Record<string, number> = {};
  experiences.forEach((exp) => {
    const typeIds = exp.payload.product_license_type_ids || [];
    typeIds.forEach((id: number) => {
      const key = id.toString();
      licenseTypeIdCounts[key] = (licenseTypeIdCounts[key] || 0) + 1;
    });
  });

  return serverOptions
    .map((opt) => ({
      value: opt.value,
      label: opt.label,
      count: licenseTypeIdCounts[opt.value] || 0,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
};
